'use client'
import { useState } from 'react'
import { copyTextToClipboard } from 'src/_helpers/copyToClipboard'
import Typography from './Typography'

const COLOR_SWATCHES = [
  {
    name: 'B&W',
    colors: [
      { name: 'White', value: '#ffffff', class: 'bg-basic-white' },
      { name: 'Black', value: '#171718', class: 'bg-basic-black' },
      { name: 'Gray1', value: '#434343', class: 'bg-basic-gray1' },
      { name: 'Gray2', value: '#636363', class: 'bg-basic-gray2' },
      { name: 'Gray3', value: '#848484', class: 'bg-basic-gray3' },
      { name: 'Gray4', value: '#9a9a9a', class: 'bg-basic-gray4' },
      { name: 'Gray5', value: '#b6b6b6', class: 'bg-basic-gray5' },
      { name: 'Gray6', value: '#c7c7c7', class: 'bg-basic-gray6' },
      { name: 'Gray7', value: '#d4d4d4', class: 'bg-basic-gray7' },
      { name: 'Gray8', value: '#e5e5e5', class: 'bg-basic-gray8' },
      { name: 'Gray10', value: '#eeeeee', class: 'bg-basic-gray10' },
    ],
  },
  {
    name: 'Dark Blue',
    colors: [
      { name: 'Dark Blue1', value: '#0b1347', class: 'bg-dark-blue-dark-blue1' },
      { name: 'Dark Blue2', value: '#202b7c', class: 'bg-dark-blue-dark-blue2' },
      { name: 'Dark Blue3', value: '#0244e8', class: 'bg-dark-blue-dark-blue3' },
      { name: 'Dark Blue4', value: '#86a8fe', class: 'bg-dark-blue-dark-blue4' },
      { name: 'Dark Blue5', value: '#b0c7ff', class: 'bg-dark-blue-dark-blue5' },
      { name: 'Dark Blue6', value: '#eaeeff', class: 'bg-dark-blue-dark-blue6' },
    ],
  },
  {
    name: 'Blue',
    colors: [
      { name: 'Blue1', value: '#049bc8', class: 'bg-basic-blue-blue1' },
      { name: 'Blue2', value: '#0db4e5', class: 'bg-basic-blue-blue2' },
      { name: 'Blue3', value: '#31cdeb', class: 'bg-basic-blue-blue3' },
      { name: 'Blue4', value: '#7deaff', class: 'bg-basic-blue-blue4' },
      { name: 'Blue5', value: '#a8f1ff', class: 'bg-basic-blue-blue5' },
    ],
  },
  {
    name: 'Green',
    colors: [
      { name: 'Green1', value: '#008f64', class: 'bg-green-green1' },
      { name: 'Green2', value: '#25ca99', class: 'bg-green-green2' },
      { name: 'Green3', value: '#83ebcc', class: 'bg-green-green3' },
      { name: 'Green4', value: '#b4fbd3', class: 'bg-green-green4' },
      { name: 'Green5', value: '#d6ebd8', class: 'bg-green-green5' },
      { name: 'Green6', value: '#e9f6eb', class: 'bg-green-green6' },
    ],
  },
  {
    name: 'Blueish',
    colors: [
      { name: 'Blueish1', value: '#3d507f', class: 'bg-blueish-blueish1' },
      { name: 'Blueish2', value: '#abb3d0', class: 'bg-blueish-blueish2' },
      { name: 'Blueish3', value: '#c4cade', class: 'bg-blueish-blueish3' },
      { name: 'Blueish4', value: '#dde2f2', class: 'bg-blueish-blueish4' },
      { name: 'Blueish5', value: '#eaeeff', class: 'bg-blueish-blueish5' },
    ],
  },
  {
    name: 'Functional',
    colors: [
      { name: 'Focus', value: '#7f29ef', class: 'bg-functional-focus-color' },
      { name: 'Error Red1', value: '#870c21', class: 'bg-functional-error-red1' },
      { name: 'Error Red2', value: '#e40900', class: 'bg-functional-error-red2' },
      { name: 'Error Red3', value: '#ff9faf', class: 'bg-functional-error-red3' },
      { name: 'Error Red4', value: '#ffc6d0', class: 'bg-functional-error-red4' },
      { name: 'Error Red5', value: '#fae6e9', class: 'bg-functional-error-red5' },
    ],
  },
]

const ColorSwatch = ({ name, value, class: className }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    const success = await copyTextToClipboard(className)
    if (!success) return

    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className={`w-112 h-112 rounded-lg border ${className}`} style={{ backgroundColor: value }} />

      <div className="text-center space-y-2 w-full">
        <Typography variant="body4" className="text-basic-black">
          {name}
        </Typography>
        <Typography variant="caption" className="text-basic-gray3">
          {value}
        </Typography>

        <button
          onClick={handleCopy}
          className="w-full px-2 py-1 text-xs bg-basic-gray6 hover:bg-basic-gray5 text-basic-black rounded transition-colors"
        >
          {copied ? 'Copied!' : 'Copy Class'}
        </button>

        <div className="bg-basic-white rounded p-2">
          <code className="text-xs text-basic-gray3 block break-all">{className}</code>
        </div>
      </div>
    </div>
  )
}

export default function ColorPalette() {
  return (
    <div className="space-y-40">
      <Typography variant="h2">Kolory</Typography>

      {COLOR_SWATCHES.map((section) => (
        <section key={section.name} className="space-y-16">
          <Typography variant="h3" className="h4--caps">
            {section.name}
          </Typography>

          <div className="grid grid-cols-2 t:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-24">
            {section.colors.map((color) => (
              <ColorSwatch key={color.name} name={color.name} value={color.value} class={color.class} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
